import React from 'react';
import { motion } from 'framer-motion';
import { COPY } from '../../contracts/copyMap';
import MeshPortrait from './MeshPortrait';
import FeatureHighlightStack from './FeatureHighlightStack';
import AurumButton from './AurumButton';
import logoWide from '../../assets/logo-wide-t.png';

// The signed-out mobile splash. Owns the whole first screen, then hands over to
// the auth panel on tap.
//
// WHY IT LOOKS LIKE THIS
// 87% of sessions land here and 70.5% bounce. On a phone the page used to be a
// logo, a four-word tagline and a login form — everything else was `hidden lg:*`.
// A visitor from cold search could not tell what the product was without
// creating an account first.
//
//   ┌──────────────────────────┐
//   │        [ MOGGED ]        │   wordmark (the page's <h1>)
//   │                          │
//   │   full-bleed portrait    │   edge-to-edge, no side gutter
//   │   + aurum face mesh      │
//   │ ▓▓ UNLOCK YOUR POTENTIAL ▓▓  the master brand element, on the eye-line
//   │                          │
//   │   ░ scrim ░              │   image fades into obsidian
//   │  ◯ PRECISION SCANS       │
//   │  ◯ PERSONALIZED GROWTH   │   what the product does, in three NAMES —
//   │  ◯ PRIVATE & ANONYMOUS   │   `compact`, so no sub-lines (see below)
//   │  [ SUBMIT YOUR PORTRAIT ]│
//   │  Already a member? Log in│
//   └──────────────────────────┘
//
// FULL-BLEED IS THE POINT. The portrait is the product — a face, read by the
// mesh, censored by the bar. Boxed into a card next to copy it looked like
// stock photography. Edge-to-edge it reads as the scan itself.
//
// The censor bar keeps the MANIFESTO ("UNLOCK YOUR POTENTIAL"), not a score.
// It is the master brand element (DESIGN.md §1) and it belongs on the face.
//
// NO SUBTEXT, AND TIGHT MARGINS. The stack is bottom-anchored under a portrait
// that bleeds off the top, so its own height decides where the content starts.
// At three names PLUS three sub-lines, on a 375x812 phone it climbed back up
// onto the face: the sub-lines sat on the gold mesh and neither the copy nor
// the portrait read. `compact` drops them, and the padding here is deliberately
// tighter than the app's usual mobile rhythm (§17.8) for the same reason — this
// is the one surface where vertical space is the constraint and the image
// behind the text is content rather than decoration.
//
// The names alone still carry the claims; the sub-lines were elaboration. The
// desktop hero keeps both, where there is a column to put them in.
//
// NO SAMPLE SCORES. An earlier pass put an example verdict here — an overall
// number plus a list of scored vectors. It was removed: numbers over the
// portrait fought the face for attention, and an invented score is the one
// thing on this page a visitor cannot verify. The three claims do the
// explaining without asking anyone to trust a fabricated 94.
//
// TWO ENTRY POINTS, deliberately. JOIN opens the sign-up tab and a secondary
// link opens log-in. A single JOIN button would make returning members hunt,
// and returning direct visitors are a disproportionate share of the affiliate
// clicks the product actually earns.
//
// Props:
//   onEnterAuth: (mode) => void  dismiss the splash and select an auth tab
const EASE = [0.16, 1, 0.3, 1];

const MobileIntro = ({ onEnterAuth = () => {} }) => (
  <motion.div
    data-testid="mobile-intro"
    // z-20 puts the splash above the hero's ≥1280px backdrop AND its film-grain
    // overlay (z-[1]). Without it the grain sat on top and greyed it out.
    //
    // Height subtracts the fixed PublicNav strip. A full 100dvh inside a parent
    // that already reserves 60px for the nav overflowed by exactly that much,
    // pushing "Already a member?" underneath the tab bar. dvh (not vh) so mobile
    // browser chrome is excluded and the CTA lands in the thumb zone.
    className="lg:hidden relative z-20 flex w-full min-h-[calc(100dvh-60px-env(safe-area-inset-bottom))] flex-col overflow-hidden"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1, transition: { duration: 0.5, ease: EASE } }}
  >
    {/* Portrait, pinned to the top and bled to both edges.
        Its own vignette is off — a centred radial fights a full-bleed image
        that has to dissolve downward into content. And its internal scrim is
        dialled well below the desktop default: here the photo IS the hero, and
        it already sits under the gradient below plus the page's film grain.
        At the default 50 all three stacked and crushed the face to near-black. */}
    <div className="pointer-events-none absolute inset-x-0 top-0 z-0">
      <MeshPortrait className="w-full" vignette={false} scrim={18} />
    </div>

    {/* Bottom-weighted scrim. Nearly clear across the face so it actually
        reads, then hard to obsidian where the content begins — without that the
        gold mesh runs underneath the text and both lose.

        Its stops are a fraction of the VIEWPORT, which is why it is not enough
        on its own: the portrait is sized by width, so the shorter the screen
        the larger the share of it the face occupies, and on a 360x640 the
        content has to start while the jaw is still there. The second scrim,
        anchored to the content block itself, is what actually guarantees the
        text never sits on the mesh. */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-[1]"
      style={{
        background:
          'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 44%, rgba(0,0,0,0.82) 66%, #000 82%)',
      }}
    />

    <div className="relative z-10 flex flex-1 flex-col px-5 pt-8 pb-6">
      {/* The page that receives 87% of all sessions had no <h1> at all — the
          logo was a bare <img>. The visible mark is unchanged; the heading is
          what search and screen readers actually read. */}
      <h1 className="m-0 flex justify-center">
        <span className="sr-only">{COPY.SIGN_IN_H1}</span>
        <img
          src={logoWide}
          alt=""
          aria-hidden="true"
          className="block h-auto w-full max-w-[260px]"
        />
      </h1>

      {/* mt-auto pushes the block to the bottom regardless of portrait height,
          so the CTA lands in the thumb zone on every phone. */}
      <div className="relative mt-auto flex w-full flex-col items-center">
        {/* CONTENT-ANCHORED SCRIM. Travels with the block, so wherever the
            layout puts it — tall phone, short phone, larger text — the ground
            behind the copy is solid and the one above it is clear.

            inset-x-[-20px] cancels the container's px-5 so it bleeds to both
            edges like the portrait does; -top-16 gives the fade somewhere to
            happen above the first row. -z-10 is safe here: the parent's `z-10`
            opens a stacking context, so this sits behind the copy but still in
            front of the portrait and the viewport scrim outside it. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-[-20px] -top-24 bottom-[-24px] -z-10"
          style={{
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.92) 28%, #000 46%)',
          }}
        />

        <FeatureHighlightStack className="flex w-full max-w-[480px]" delayFrom={2} compact />

        <div className="mt-7 w-full max-w-[480px]">
          <AurumButton
            onClick={() => onEnterAuth('sign-up')}
            data-testid="intro-join"
            fullWidth
            size="lg"
          >
            {COPY.INTRO_JOIN_CTA}
          </AurumButton>

          <p
            className="m-0 mt-4 text-center font-display text-bone-dim"
            style={{ fontSize: 14 }}
          >
            {COPY.INTRO_MEMBER_PREFIX}{' '}
            <button
              type="button"
              onClick={() => onEnterAuth('sign-in')}
              data-testid="intro-login"
              className="font-display text-aurum underline underline-offset-2 transition-colors hover:text-aurum-bright"
            >
              {COPY.INTRO_MEMBER_CTA}
            </button>
          </p>
        </div>
      </div>
    </div>
  </motion.div>
);

export default MobileIntro;
